/**
 * The link candidate (GET /work-items/{id}/link-candidate/{clientId}):
 * what Client Setup's confirm step shows about an existing client before an
 * intake card is linked to it. The read is audited on the server.
 */

import { useQuery } from '@tanstack/react-query';
import { apiErrorFrom } from './api';
import type { ClientEngagementValue } from './clientsApi';

export interface LinkCandidate {
  client_id: string;
  display_name: string;
  preferred_name: string | null;
  engagement: ClientEngagementValue;
  pronouns: string | null;
  caretree_external_id: string | null;
  primary_cm_name: string | null;
  /** 'YYYY-MM-DD', or null when not recorded. */
  dob: string | null;
  home: { city: string | null; zip: string | null } | null;
  intake_card_count: number;
}

export async function fetchLinkCandidate(
  slug: string,
  workItemId: string,
  clientId: string,
  signal?: AbortSignal,
): Promise<LinkCandidate> {
  const path = `/api/t/${encodeURIComponent(slug)}/v1/work-items/${encodeURIComponent(workItemId)}/link-candidate/${encodeURIComponent(clientId)}`;
  const res = await fetch(path, { method: 'GET', headers: { Accept: 'application/json' }, signal });
  if (!res.ok) throw await apiErrorFrom(res, `GET ${path}`);
  return (await res.json()) as LinkCandidate;
}

export function useLinkCandidate(slug: string, workItemId: string | null, clientId: string | null) {
  return useQuery({
    queryKey: ['linkCandidate', slug, workItemId, clientId],
    queryFn: ({ signal }) => fetchLinkCandidate(slug, workItemId!, clientId!, signal),
    enabled: workItemId !== null && clientId !== null,
    retry: false,
  });
}
